import axios, { type AxiosResponse } from 'axios';
import type { RefreshOkDto } from 'viewup-api';
import { clientTypeHeaders } from '../constants';

export const viewupAxios = <T = unknown>() => {
  const _viewupAxios = axios.create({
    baseURL: process.env.NEXT_PUBLIC_VIEWUP_API_URL,
    headers: {
      ...clientTypeHeaders,
      'viewup-viewport-height': window.innerHeight,
      'viewup-viewport-width': window.innerWidth,
      'x-httpClient': 'viewupAxios',
    },
    withCredentials: true,
  });

  const accessToken = window.localStorage.getItem('token') || '';

  _viewupAxios.interceptors.request.use(
    (config) => {
      config.headers = JSON.parse(JSON.stringify(config.headers || {}));

      if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;

      return config;
    },
    (error) => Promise.reject(error),
  );

  _viewupAxios.interceptors.response.use(
    (config) => {
      return config;
    },
    async (error) => {
      const isRefreshing = localStorage.getItem('isRefreshing');
      const lastRefreshed = localStorage.getItem('lastRefreshed');
      const token = localStorage.getItem('token');
      const code = error?.response?.data?.code;

      if (
        error?.response?.data?.code === 'auth-009' ||
        error?.response?.data?.code === 'adminAuth-008'
      ) {
        if (!token) {
          return Promise.reject(error);
        }

        if (isRefreshing && isRefreshing === 'true') {
          return Promise.reject({ response: { data: { code: '/isRefresh' } } });
        }

        if (
          localStorage.getItem('lastRefreshed') &&
          Date.now() - Number(lastRefreshed) < 60000 * 0.1
        ) {
          return Promise.reject({ response: { data: { code: '/' } } });
        } else {
          localStorage.setItem('lastRefreshed', Date.now().toString());
          localStorage.setItem('isRefreshing', 'true');

          try {
            const refreshTokenRes = await axios.get<undefined, AxiosResponse<RefreshOkDto>>(
              code === 'auth-009' ? '/auth/refresh' : '/admin/auth/refresh',
              {
                baseURL: process.env.NEXT_PUBLIC_VIEWUP_API_URL,
                headers: { ...clientTypeHeaders },
                withCredentials: true,
              },
            );

            const token = refreshTokenRes.data.data.accessToken || '';
            localStorage.setItem('token', token);
            localStorage.setItem('isRefreshing', 'false');
            if (error.config.method !== 'get') {
              const res = await axios({
                baseURL: process.env.NEXT_PUBLIC_VIEWUP_API_URL,
                data: JSON.parse(error.config.data),
                headers: {
                  ...error.config.headers,
                  Authorization: `Bearer ${token}`,
                },
                method: error.config.method,
                url: error.config.url,
                withCredentials: true,
              });
              return res;
            } else {
              localStorage.setItem('isRefreshing', 'false');
              window.location.reload();
              return Promise.reject({ response: { data: { code: 'refresh' } } });
            }
          } catch (e) {
            return Promise.reject(e);
          }
        }
      } else {
        return Promise.reject(error);
      }
    },
  );

  return _viewupAxios<T>;
};
